import React, { useState, useRef, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';

interface OtpInputProps {
  length: number;
  onComplete: (otp: string) => void;
  isSubmitting?: boolean;
}

export default function OtpInput({ length, onComplete, isSubmitting = false }: OtpInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize refs array
  if (inputRefs.current.length !== length) {
    inputRefs.current = Array(length).fill(null);
  }

  const focusInput = (index: number) => {
    if (index >= 0 && index < length) {
      inputRefs.current[index]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text/plain').trim();
    
    // Only process if the pasted content has at least the required digits
    if (pasteData.length >= length && /^\d+$/.test(pasteData)) {
      // Take the last n digits if longer than needed
      const digits = pasteData.slice(-length).split('');
      setOtp(digits);
      inputRefs.current[length - 1]?.focus();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    
    // Only allow single digit input
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Auto-focus next input if a digit was entered
      if (value && index < length - 1) {
        focusInput(index + 1);
      }
      
      // Check if OTP is complete for auto-submission
      const otpValue = newOtp.join('');
      if (otpValue.length === length && !otpValue.includes('')) {
        // Auto-submit is handled in the onSubmit function below
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!otp[index]) {
        // If current input is empty, focus previous input
        focusInput(index - 1);
      } else {
        // Clear current input
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    } 
    // Handle arrow keys
    else if (e.key === 'ArrowLeft') {
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight') {
      focusInput(index + 1);
    }
  };

  const handleSubmit = () => {
    const otpValue = otp.join('');
    if (otpValue.length === length) {
      onComplete(otpValue);
    }
  };

  // Check if all inputs are filled
  const isComplete = otp.join('').length === length;

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex justify-center gap-1.5 sm:gap-2 md:gap-3 my-3 sm:my-4">
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={otp[index]}
            ref={(el: HTMLInputElement | null): void => {
              inputRefs.current[index] = el;
            }}
            onChange={e => handleChange(e, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            onPaste={index === 0 ? handlePaste : undefined}
            className="w-9 h-11 sm:w-11 sm:h-13 md:w-12 md:h-14 border-2 rounded-lg text-center text-lg sm:text-xl font-bold 
                       border-zinc-300 bg-white text-zinc-900 dark:border-zinc-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 
                       dark:bg-zinc-900 dark:text-white transition-colors duration-200 
                       hover:border-zinc-400 dark:hover:border-zinc-600"
            placeholder=""
            aria-label={`OTP digit ${index + 1}`}
          />
        ))}
      </div>
      
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting || !isComplete}
        className={`mt-3 sm:mt-4 w-full sm:w-auto px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base
                  ${isComplete ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400'} 
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 transition-colors duration-200
                  ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
      >
        {isSubmitting ? 'Verifying...' : 'Verify OTP'}
      </button>
    </div>
  );
} 